// Pure seat math and validation for the member portal's High Holiday
// section. Mirrors kbt-backend's me/highHolidays.js so the browser can
// validate and preview the cost before round-tripping — the backend
// remains the authority on both (same split as
// src/service/seatPricing.js and guestSeatValidation.js).

const named = (values) =>
  (Array.isArray(values) ? values : []).filter(
    (v) => typeof v === 'string' && v.trim().length > 0
  );

// Included seats come with the membership. A member may confirm
// attendance without naming anyone yet, so zero is valid — only going
// OVER the allowance is not.
export const validateIncludedSeats = (seatNames, allowance) => {
  const count = named(seatNames).length;
  if (count > allowance) {
    return {
      valid: false,
      error: `Your membership includes ${allowance} seat(s). Add guest seats below for anyone else.`,
    };
  }
  return { valid: true, error: null };
};

// Guest seats are a purchase, so an empty list is a certain 422 from the
// backend. Catching it here means a useful message instead of a
// round-trip to a known failure — the same lesson as
// guestSeatValidation.js#validateGuestSeatCount.
const GENDERS = ['male', 'female'];

// Every named person must have an explicit men's/women's section. Seating
// is separate, so this is NOT a field to guess at: defaulting an
// unspecified person to the men's section (which is what
// seatPricing.js#countSeatsByGender does for the older flow) could seat a
// woman on the men's side, and seatRowsToBlock's filter would otherwise
// drop the row silently — the member types a name, nothing is sent, and
// their guest arrives to no seat at all. Asking is the only safe option.
//
// Exported separately from validateGuestSeats because the INCLUDED seats
// need exactly this check and none of the rest: a member may confirm
// attendance having named nobody yet (validateIncludedSeats allows zero),
// so validateGuestSeats' "at least one" rule cannot be reused there — but
// seatRowsToBlock drops a sectionless row just as silently on that path.
export const validateSeatSections = (seatRows) => {
  const rows = Array.isArray(seatRows) ? seatRows : [];
  const missingSection = rows.filter(
    (r) =>
      typeof r?.name === 'string' && r.name.trim().length > 0 && !GENDERS.includes(r?.gender)
  );
  if (missingSection.length > 0) {
    const who = missingSection.map((r) => r.name.trim()).join(', ');
    return {
      valid: false,
      error: `Choose men's or women's seating for: ${who}.`,
    };
  }
  return { valid: true, error: null };
};

export const validateGuestSeats = (seatRows) => {
  const rows = Array.isArray(seatRows) ? seatRows : [];
  const named = rows.filter((r) => typeof r?.name === 'string' && r.name.trim().length > 0);
  if (named.length === 0) {
    return { valid: false, error: 'Add at least one guest seat to continue.' };
  }

  return validateSeatSections(rows);
};

export const computeMemberSeatCost = (seatCount, pricePerSeat) => {
  const count = Number.isFinite(seatCount) && seatCount > 0 ? Math.floor(seatCount) : 0;
  // Guard the price as well as the count. If the backend payload ever
  // arrived without memberSeatPrice, an unguarded multiply renders "$NaN"
  // in front of a congregant on a checkout screen.
  const price = Number.isFinite(pricePerSeat) && pricePerSeat >= 0 ? pricePerSeat : 0;
  return { seatCount: count, pricePerSeat: price, total: count * price };
};

// The inverse of seatRowsToBlock: turn a stored `{ menSeatNames,
// womenSeatNames }` block back into the name+section rows the form edits,
// padded out to `minRows` blank rows so a member with an allowance of 2
// and one name on file still sees a second empty field to fill.
//
// Blank rows are padded with gender: '' rather than a default of 'male'.
// An unset section is what makes validateSeatSections ASK, and asking is
// the whole point — see the note there.
export const seatBlockToRows = (block, minRows = 0) => {
  const rows = [
    ...named(block?.menSeatNames).map((name) => ({ name: name.trim(), gender: 'male' })),
    ...named(block?.womenSeatNames).map((name) => ({ name: name.trim(), gender: 'female' })),
  ];
  const wanted = Number.isFinite(minRows) && minRows > 0 ? Math.floor(minRows) : 0;
  const pad = Math.max(0, wanted - rows.length);
  return [...rows, ...Array.from({ length: pad }, () => ({ name: '', gender: '' }))];
};

export const seatRowsToBlock = (seatRows) => {
  const rows = Array.isArray(seatRows) ? seatRows : [];
  const pick = (gender) =>
    rows
      .filter((r) => r?.gender === gender && typeof r?.name === 'string' && r.name.trim())
      .map((r) => r.name.trim());
  return { menSeatNames: pick('male'), womenSeatNames: pick('female') };
};
